import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';

import { DefaultLoadingUi, RestaurantShowCase } from '../../components/restaurant-showcase';
import {
  RestaurantStaticType,
  fetchRestaurantInformation,
} from '../../components/restaurant-showcase/server-side/fetch-restaurant-information';
import { RestaurantNotFound } from '../../components/restaurant-showcase/components/restaurant-not-found';
import { getServerTranslations } from '../../lib/i18n/getServerTranslations';

import { createRestaurantsService } from '@restworld/data-services';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

export const LocalePage = (props: RestaurantPageProps) => {
  const router = useRouter();
  if (router.isFallback) {
    return <DefaultLoadingUi />;
  } else if (props.restaurantStatic) {
    return <RestaurantShowCase {...props.restaurantStatic} />;
  } else {
    return <RestaurantNotFound />;
  }
};

export default LocalePage;

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const restaurants = await createRestaurantsService({ baseUrl }).fetchRestaurantIds({
      limit: 1000,
      showDetails: true,
    });
    const paths = Array.isArray(restaurants?.data)
      ? restaurants?.data?.reduce((acc: string[], restaurant) => {
          acc = [...acc, ...(restaurant.slug ? [`/locale/${restaurant.slug}`] : [])];
          return acc;
        }, [])
      : [];
    return {
      paths: paths ?? [],
      fallback: true,
    };
  } catch (error) {
    console.error(error, 'error fetching restaurant for paths');
    return {
      paths: null,
      fallback: true,
    };
  }
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const slug: string = params?.slug as string;
  if (!slug) {
    return {
      redirect: {
        destination: '/locale/not-found',
        permanent: false,
      },
    };
  }
  const staticProps = {
    revalidate: 60,
    props: {
      restaurantStatic: null,
      errorCode: null,
    },
  };
  const transConfig = await getServerTranslations(locale, [
    'common',
    'restaurant-showcase-page',
    'auth-flow',
    'onboarding-page',
  ]);
  try {
    const restaurantProccessed = await fetchRestaurantInformation(slug);
    return {
      ...staticProps,
      props: {
        ...staticProps.props,
        ...transConfig,
        restaurantStatic: restaurantProccessed,
      },
    };
  } catch (error) {
    console.error(`Error getting static restaurant data for ${slug}`, error);
    return {
      ...staticProps,
      props: {
        ...staticProps.props,
        ...transConfig,
        restaurantStatic: null,
      },
    };
  }
};

export interface RestaurantPageProps {
  restaurantStatic: RestaurantStaticType | null;
  errorCode?: number | null;
}
